import React, { useState, useEffect, useCallback } from 'react';
import { I18n } from 'react-redux-i18n';
import { Form, Button, Modal, Nav } from 'react-bootstrap';
import { HiOutlineArrowLeft, HiOutlineTrash } from 'react-icons/hi';
import { useDynamicFormResolver } from './hooks/useDynamicFormResolver';
import DynamicFormEngine from './components/DynamicFormEngine';
import CCModal2025 from '../../../../../../components/ccModal2025';
import {
  getPayoutFleetMethods,
  createBankAccountGsn,
  removeBankAccountGsn,
} from './api/payoutMethodApi';
import './NetWorkPaymentMethod.scss';
import banktransper from '../../../../../../assets/images/icons/banktransper.svg';
import paypal from '../../../../../../assets/images/icons/paypal.png';
import wise from '../../../../../../assets/images/icons/wise.png';
import {
  getPayoutMethodKind,
  getPayoutMethodDisplayName,
  PAYOUT_KIND,
} from './utils/payoutMethodUtils';

const VIEW_LIST = 'list';
const VIEW_ADD = 'add';

const PAYOUT_METHOD_OPTIONS = [
  {
    value: 'Bank Transfer',
    labelKey: 'billingUsage.payoutMethod.bankTransfer',
  },
  { value: 'Wise', labelKey: 'billingUsage.payoutMethod.wise' },
  { value: 'Paypal', labelKey: 'billingUsage.payoutMethod.paypal' },
];

/** Mask connectToken for display: email -> show as-is (or masked), number -> last 4, @tag -> show */
function maskConnectToken(gateway, connectToken) {
  if (!connectToken) return '—';
  const s = String(connectToken).trim();
  if (gateway === 'PayPal' && s.includes('@')) return s;
  if (gateway === 'Wise' && s.startsWith('@')) return s;
  if (/^\d+$/.test(s) && s.length >= 4) return `**** ${s.slice(-4)}`;
  if (s.length > 8) return `${s.slice(0, 4)}****${s.slice(-4)}`;
  return '**** ' + s.slice(-4);
}

function renderPayoutMethodIcon(method, className) {
  const kind = getPayoutMethodKind(method);
  if (kind === PAYOUT_KIND.PAYPAL) {
    return <img src={paypal} alt="PayPal" className={className} />;
  }
  if (kind === PAYOUT_KIND.WISE) {
    return <img src={wise} alt="Wise" className={className} />;
  }
  return (
    <img src={banktransper} alt="Bank Transfer" className={className} />
  );
}

const NetWorkPaymentMethod = ({
  auth,
  commonData,
  isShow,
  onClose,
  notification,
  loadingActions,
  currencyISO,
}) => {
  const fleetId = auth?.selectedFleet?.fleetId ?? '';
  const gateway = commonData?.hydraSetting?.hydraPenaltyPolicy?.gateway; // AirWallex or Wise

  const [view, setView] = useState(VIEW_LIST);
  const [methods, setMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add form: payout method type (Bank Transfer | Wise | Paypal)
  const [addPayoutMethodType, setAddPayoutMethodType] =
    useState('Bank Transfer');
  const [wiseTag, setWiseTag] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');

  const {
    schema,
    activeSchema,
    isWiseMultiVariant,
    wiseActiveVariantKey,
    selectWiseVariant,
    formData,
    setFieldValue,
    errors,
    loading,
    refreshLoading,
    refreshPending,
    triggerRefresh,
    validate,
    resetForm,
    fetchSchema,
  } = useDynamicFormResolver({ fleetId, type: 'fleet', gateway, notification, loadingActions, currencyISO });

  const fetchMethods = useCallback(async () => {
    if (!fleetId) return;
    setLoadingMethods(true);
    try {
      const list = await getPayoutFleetMethods({ fleetId });
      setMethods(list);
    } catch (err) {
      notification(
        'error',
        err?.message || I18n.t('messages.commonMessages.error'),
      );
      setMethods([]);
    } finally {
      setLoadingMethods(false);
    }
  }, [fleetId, notification]);

  useEffect(() => {
    if (isShow && fleetId) {
      fetchMethods();
      setView(VIEW_LIST);
    }
  }, [isShow, fleetId]);

  useEffect(() => {
    if (
      view === VIEW_ADD &&
      addPayoutMethodType === 'Bank Transfer' &&
      isShow &&
      fleetId
    ) {
      fetchSchema();
    }
  }, [view, addPayoutMethodType, isShow, fleetId]);

  // Global loading spinner when loading payout methods list
  useEffect(() => {
    if (!loadingActions) return;
    if (loadingMethods) {
      loadingActions.showLoadingSpiner();
    } else {
      loadingActions.hideLoadingSpiner();
    }
    return () => {
      if (loadingMethods) loadingActions.hideLoadingSpiner();
    };
  }, [loadingMethods, loadingActions]);

  const handleClose = () => {
    setView(VIEW_LIST);
    setAddPayoutMethodType('Bank Transfer');
    setWiseTag('');
    setPaypalEmail('');
    resetForm();
    setSubmitError(null);
    onClose();
  };

  const goToAdd = () => {
    setAddPayoutMethodType('Bank Transfer');
    setWiseTag('');
    setPaypalEmail('');
    setSubmitError(null);
    resetForm();
    setView(VIEW_ADD);
  };

  const goBackToList = () => {
    setView(VIEW_LIST);
    setSubmitError(null);
    resetForm();
  };

  const handleDeleteClick = (e, method) => {
    e.stopPropagation();
    setMethodToDelete(method);
  };

  const handleCloseDeleteConfirm = () => {
    if (!deleting) setMethodToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!methodToDelete || !fleetId) return;
    setDeleting(true);
    loadingActions?.showLoadingSpiner?.();
    try {
      const res = await removeBankAccountGsn({
        fleetId,
        token: methodToDelete.connectToken,
      });
      if (res?.res?.returnCode === 200) {
        notification('success', res?.message || I18n.t('billingUsage.payoutMethod.DeleteMethodSuccess'));
        setMethodToDelete(null);
        await fetchMethods();
      } else {
        const errorMessage =
          res?.res?.response?.message ||
          res?.res?.message ||
          res?.error?.message ||
          I18n.t('billingUsage.payoutMethod.DeleteMethodError');
        notification('error', errorMessage);
      }
    } catch (err) {
      notification('error', err?.message || I18n.t('billingUsage.payoutMethod.DeleteMethodError'));
    } finally {
      setDeleting(false);
      loadingActions?.hideLoadingSpiner?.();
    }
  };

  const validateAddForm = () => {
    if (addPayoutMethodType === 'Wise') {
      const tag = (wiseTag || '').trim();
      if (!tag) {
        setSubmitError(I18n.t('billingUsage.payoutMethod.wiseTagRequired'));
        return false;
      }
      setSubmitError(null);
      return true;
    }
    if (addPayoutMethodType === 'Paypal') {
      const email = (paypalEmail || '').trim();
      if (!email) {
        setSubmitError(I18n.t('billingUsage.payoutMethod.paypalEmailRequired'));
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setSubmitError(I18n.t('billingUsage.payoutMethod.paypalEmailInvalid'));
        return false;
      }
      setSubmitError(null);
      return true;
    }
    if (addPayoutMethodType === 'Bank Transfer') {
      setSubmitError(null);
      return validate();
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validateAddForm()) return;

    setSubmitting(true);
    loadingActions?.showLoadingSpiner?.();
    try {
      let payload = { fleetId, type: 'fleet' };
      if (addPayoutMethodType === 'Bank Transfer') {
        payload = {
          ...payload,
          formData,
          fields: activeSchema?.fields ?? [],
          gateway,
          schema: activeSchema,
        };
      } else if (addPayoutMethodType === 'Wise') {
        let wiseId = (wiseTag || '').trim();
        if (wiseId && !wiseId.startsWith('@')) wiseId = `@${wiseId}`;
        payload.wiseIdentifier = wiseId;
      } else if (addPayoutMethodType === 'Paypal') {
        payload.payPalIdentifier = (paypalEmail || '').trim();
      }

      const response = await createBankAccountGsn(payload);
      if (response?.res?.returnCode === 200) {
        notification(
          'success',
          response?.message || I18n.t('billingUsage.payoutMethod.AddMethodSuccess'),
        );
        await fetchMethods();
        goBackToList();
      } else {
        const errorMessage =
          response?.res?.response?.message ||
          response?.res?.message ||
          response?.error?.message ||
          I18n.t('billingUsage.payoutMethod.AddMethodError');
        notification('error', errorMessage);
      }
    } catch (err) {
      notification(
        'error',
        err?.message || I18n.t('billingUsage.payoutMethod.AddMethodError'),
      );
    } finally {
      setSubmitting(false);
      loadingActions?.hideLoadingSpiner?.();
    }
  };

  const isBankTransfer = addPayoutMethodType === 'Bank Transfer';
  const isWise = addPayoutMethodType === 'Wise';
  const isPaypal = addPayoutMethodType === 'Paypal';
  const showSubmitInAdd =
    view === VIEW_ADD && (isBankTransfer || isWise || isPaypal);

  return (
    <>
    <CCModal2025
      isShow={isShow}
      onClose={handleClose}
      // title={view === VIEW_LIST ? I18n.t('billingUsage.payoutMethod.title') : I18n.t('billingUsage.payoutMethod.addNewMethod')}
      title={
        <div className="net-work-payment-method-add-header">
          {view !== VIEW_LIST && (
            <button
              type="button"
              className="btn-back"
              onClick={goBackToList}
              aria-label="Back"
            >
              <HiOutlineArrowLeft size={20} />
            </button>
          )}
          <span className="add-title">
            {view === VIEW_LIST
              ? I18n.t('billingUsage.payoutMethod.title')
              : I18n.t('billingUsage.payoutMethod.addNewMethod')}
          </span>
        </div>
      }
      onSubmit={view === VIEW_LIST ? () => {} : handleSubmit}
      btnSubmitText={I18n.t('billingUsage.payoutMethod.saveMethod')}
      disabledSubmit={loading || refreshLoading || submitting}
      hideSubmitButton={view === VIEW_LIST}
      hideFooter={view === VIEW_LIST}
    >
      {view === VIEW_LIST && (
        <div className="net-work-payment-method-list">
          {loadingMethods ? (
            <div className="text-muted py-4 text-center">
              {I18n.t('networkSetting.Loading')}
            </div>
          ) : methods.length === 0 ? (
            <div className="net-work-payment-method-empty">
              <div className="net-work-payment-method-empty-icon">
                <img
                  src={banktransper}
                  alt=""
                  className="payout-method-icon payout-method-icon-empty"
                />
              </div>
              <p className="net-work-payment-method-empty-title">
                {I18n.t('billingUsage.payoutMethod.noMethodsYet')}
              </p>
              <p className="net-work-payment-method-empty-desc">
                {I18n.t('billingUsage.payoutMethod.noMethodsYetDesc')}
              </p>
              <div className="net-work-payment-method-add-wrap mt-3">
                <Button
                  className="btn-add-payout-method-outline"
                  onClick={goToAdd}
                >
                  + {I18n.t('billingUsage.payoutMethod.addPayoutMethod')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="net-work-payment-method-cards">
                {methods.map((m, idx) => (
                  <div
                    key={`${m.gateway}-${m.connectToken}-${idx}`}
                    className="net-work-payment-method-card"
                  >
                    <div className="net-work-payment-method-card-icon">
                      {renderPayoutMethodIcon(m, 'payout-method-icon')}
                    </div>
                    <div className="net-work-payment-method-card-body">
                      <div className="net-work-payment-method-card-masked">
                        {maskConnectToken(m.gateway, m.connectToken)}
                      </div>
                      <div className="net-work-payment-method-card-type">
                        {getPayoutMethodDisplayName(m)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="net-work-payment-method-card-delete"
                      onClick={(e) => handleDeleteClick(e, m)}
                      aria-label={I18n.t('billingUsage.payoutMethod.deleteMethod')}
                    >
                      <HiOutlineTrash size={20} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="net-work-payment-method-add-wrap mt-3">
                <Button
                  className="btn-add-payout-method-outline"
                  onClick={goToAdd}
                >
                  + {I18n.t('billingUsage.payoutMethod.addPayoutMethod')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {view === VIEW_ADD && (
        <div className="net-work-payment-method-add">
          <div className="account-details-section">
            <Form.Group className="mb-3">
              <Form.Label>
                {I18n.t('billingUsage.payoutMethod.payoutMethodLabel')}
              </Form.Label>
              <Form.Control
                as="select"
                value={addPayoutMethodType}
                onChange={(e) => setAddPayoutMethodType(e.target.value)}
                className="form-control form-custom"
              >
                {PAYOUT_METHOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {I18n.t(opt.labelKey)}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            {isWise && (
              <>
                <a
                  href="https://wise.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-block mb-2"
                >
                  {I18n.t('billingUsage.payoutMethod.openWiseAccount')}{' '}
                  <span aria-hidden>↗</span>
                </a>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {I18n.t('billingUsage.payoutMethod.wiseTag')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control form-custom"
                    placeholder={I18n.t(
                      'billingUsage.payoutMethod.wiseTagPlaceholder',
                    )}
                    value={wiseTag}
                    onChange={(e) => setWiseTag(e.target.value)}
                  />
                </Form.Group>
              </>
            )}

            {isPaypal && (
              <>
                <a
                  href="https://www.paypal.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-block mb-2"
                >
                  {I18n.t('billingUsage.payoutMethod.openPaypalAccount')}{' '}
                  <span aria-hidden>↗</span>
                </a>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {I18n.t('billingUsage.payoutMethod.paypalEmail')}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder={I18n.t(
                      'billingUsage.payoutMethod.paypalEmailPlaceholder',
                    )}
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    className="form-control form-custom"
                  />
                </Form.Group>
              </>
            )}

            {isBankTransfer && (
              <>
                {isWiseMultiVariant && schema?.wiseVariants?.length > 0 && (
                  <Nav
                    variant="tabs"
                    className="wise-payout-variant-tabs mb-3"
                    activeKey={wiseActiveVariantKey ?? schema.wiseVariants[0]?.variantKey}
                    onSelect={(k) => k != null && selectWiseVariant(k)}
                  >
                    {schema.wiseVariants.map((v) => (
                      <Nav.Item key={v.variantKey}>
                        <Nav.Link eventKey={v.variantKey}>
                          {v.title || v.variantKey}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                )}
                <DynamicFormEngine
                  schema={activeSchema}
                  formData={formData}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  triggerRefresh={triggerRefresh}
                  loading={loading}
                  refreshLoading={refreshLoading}
                  refreshPending={refreshPending}
                  loadingActions={loadingActions}
                  currencyISO={currencyISO}
                />
              </>
            )}
          </div>

          {submitError && (
            <div className="alert alert-danger mb-3" role="alert">
              {submitError}
            </div>
          )}
        </div>
      )}
    </CCModal2025>

    <Modal
      show={!!methodToDelete}
      onHide={handleCloseDeleteConfirm}
      backdrop="static"
      className="confirm ccModal2025"
    >
      <Modal.Header closeButton>
        <Modal.Title>{I18n.t('billingUsage.payoutMethod.confirmDeleteTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {methodToDelete && (
          <p>{I18n.t('billingUsage.payoutMethod.confirmDeleteMessage')}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseDeleteConfirm} disabled={deleting}>
          {I18n.t('driver.Cancel')}
        </Button>
        <Button
          className="btn-save"
          onClick={handleConfirmDelete}
          disabled={deleting}
        >
          {deleting ? I18n.t('networkSetting.Loading') : I18n.t('billingUsage.payoutMethod.confirmDelete')}
        </Button>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default NetWorkPaymentMethod;
